import { useEffect, useState } from "react";
import { produitsApi, partenairesApi } from "@/lib/api";
import { toast } from "sonner";
import {
  PageHeader,
  Card,
  Btn,
  Field,
  FieldLabel,
  Select,
  EmptyState,
} from "@/components/DashboardUI";
import { ChefHat, Plus, X, Trash2, AlertTriangle } from "lucide-react";

const DEFAULT = {
  partenaireId: "",
  nom: "",
  categorie: "",
  quantite: "",
  stock: 0,
  actif: true,
};

export default function Produits() {
  const [items, setItems] = useState([]);
  const [partenaires, setPartenaires] = useState([]);
  const [form, setForm] = useState(DEFAULT);
  const [show, setShow] = useState(false);

  const reload = async () => {
    try {
      const [p, parts] = await Promise.all([
        produitsApi.list(),
        partenairesApi.list(),
      ]);
      setItems(p);
      setPartenaires(parts);
    } catch {
      toast.error("Erreur");
    }
  };
  useEffect(() => {
    reload();
  }, []);

  async function create(e) {
    e.preventDefault();
    try {
      await produitsApi.create(form);
      toast.success("Produit créé");
      setShow(false);
      setForm(DEFAULT);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  }

  async function remove(id) {
    if (!window.confirm("Supprimer ?")) return;
    try {
      await produitsApi.remove(id);
      toast.success("Supprimé");
      reload();
    } catch {
      toast.error("Erreur");
    }
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="produits-page">
      <PageHeader
        chapter="Chapitre pro · La box"
        title="Catalogue box"
        subtitle={`${items.length} produits référencés`}
        testId="prod-header"
        actions={
          <Btn
            variant={show ? "secondary" : "primary"}
            onClick={() => setShow(!show)}
            disabled={partenaires.length === 0}
            data-testid="btn-nouveau-produit"
          >
            {show ? (
              <>
                <X className="w-4 h-4" />
                Annuler
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Ajouter
              </>
            )}
          </Btn>
        }
      />

      {partenaires.length === 0 && (
        <div
          className="flex items-start gap-3 p-4 border border-or/40 bg-or/5 rounded-sm text-sm text-parchemin/80"
          data-testid="prod-warning"
        >
          <AlertTriangle className="w-4 h-4 text-or flex-shrink-0 mt-0.5" />
          <span>
            Créez d&apos;abord au moins un partenaire avant d&apos;ajouter des
            produits.
          </span>
        </div>
      )}

      {show && (
        <form onSubmit={create} data-testid="produit-form">
          <Card>
            <h2 className="font-display text-xl text-parchemin mb-6">
              Nouveau produit
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <FieldLabel>Partenaire</FieldLabel>
                <Select
                  required
                  data-testid="form-prod-partenaire"
                  value={form.partenaireId}
                  onChange={(e) =>
                    setForm({ ...form, partenaireId: e.target.value })
                  }
                >
                  <option value="" className="bg-noir">
                    — choisir —
                  </option>
                  {partenaires.map((p) => (
                    <option key={p.id} value={p.id} className="bg-noir">
                      {p.nom}
                    </option>
                  ))}
                </Select>
              </div>
              <Field
                label="Nom"
                value={form.nom}
                onChange={(v) => setForm({ ...form, nom: v })}
                required
                testid="form-prod-nom"
              />
              <Field
                label="Catégorie"
                value={form.categorie}
                onChange={(v) => setForm({ ...form, categorie: v })}
                required
                testid="form-prod-cat"
              />
              <Field
                label="Quantité"
                value={form.quantite}
                onChange={(v) => setForm({ ...form, quantite: v })}
                testid="form-prod-qte"
              />
              <Field
                label="Stock"
                type="number"
                value={form.stock}
                onChange={(v) =>
                  setForm({ ...form, stock: parseInt(v, 10) || 0 })
                }
                testid="form-prod-stock"
              />
            </div>
            <div className="mt-6">
              <Btn type="submit" size="lg" data-testid="form-prod-submit">
                Créer le produit
              </Btn>
            </div>
          </Card>
        </form>
      )}

      {items.length === 0 && !show ? (
        <EmptyState
          icon={<ChefHat className="w-6 h-6" />}
          title="Aucun produit pour le moment."
          testId="prod-empty"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div
              key={p.id}
              className="bg-noir-50/50 border border-or/15 hover:border-or/40 rounded-sm p-5 transition"
              data-testid={`prod-card-${p.id}`}
            >
              <div className="font-display text-lg text-parchemin">
                {p.nom}
              </div>
              <div className="text-xs font-mono text-parchemin/50 mt-1">
                {p.partenaire?.nom || "?"} · {p.categorie}
              </div>
              {p.quantite && (
                <div className="text-sm text-parchemin/70 mt-2 italic font-display">
                  « {p.quantite} »
                </div>
              )}
              <div className="flex justify-between items-center mt-5 pt-4 border-t border-or/10">
                <span className="font-mono text-xs text-parchemin/50 tracking-wider">
                  Stock :{" "}
                  <span className="text-or">{p.stock || 0}</span>
                </span>
                <Btn
                  variant="dangerText"
                  size="sm"
                  onClick={() => remove(p.id)}
                  data-testid={`prod-del-${p.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Btn>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
